// @catalog F.010
/**
 * SideFilters — panel inline derecho colapsable con 6 secciones (Fase 6).
 *
 * Spec literal:
 *   - README §4 SideFilters (líneas 162-167) — width 0/260 + transition + header sticky.
 *   - README §Comportamiento §Side filters (líneas 219-223) — intersección por categoría
 *     + counts dinámicos (filtran por el resto → más útil al usuario).
 *   - prototype/forecast-page.jsx 483-573 (render).
 *   - prototype/styles.css 242-309 (.fc-side-filters + .fc-side-head + .fc-fgroup + .fc-fcheck).
 *
 * 6 secciones (orden):
 *   1. Clase ABC (A/B/C)
 *   2. Predictibilidad XYZ (X/Y/Z)
 *   3. Patrón de demanda (smooth/erratic/intermittent/lumpy)
 *   4. Lifecycle (active/stable/declining/new)
 *   5. Cobertura DCI (full_coverage/partial_high/partial_low/minimal)
 *   6. Otros (Solo compras activas — bool)
 *
 * Comportamiento:
 *   - Container width 0 closed → 260 open con transition width 0.22s ease + flex-shrink 0.
 *   - Inner SIEMPRE 260px (mantiene layout durante animación).
 *   - PROHIBIDO <Drawer>/<Dialog> MUI (handoff regla 3).
 *   - Botón Limpiar resetea TODOS los Sets + activeOnly=false.
 *   - Counts dinámicos: para grupo X, cuenta = productos que pasan filtros activos
 *     en grupos ≠X + tienen el valor del checkbox X.
 */
import React, { useMemo } from "react";
import { Box, Stack, IconButton } from "@mui/material";
import {
  FilterList as FilterListIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import type { AdvancedFilters } from "./selectors";
import {
  countActiveAdvancedFilters,
  emptyAdvancedFilters,
} from "./selectors";
import type { ProductData } from "./types";

export type AdvancedFilterGroup = keyof Omit<AdvancedFilters, "activeOnly">;

export interface SideFiltersProps {
  open: boolean;
  onClose: () => void;
  advancedFilters: AdvancedFilters;
  onToggleFilter: (group: AdvancedFilterGroup, value: string) => void;
  onToggleActiveOnly: (next: boolean) => void;
  onClear: () => void;
  products: ProductData[];
}

interface CheckboxOption {
  value: string;
  label: string;
}

const ABC_OPTIONS: CheckboxOption[] = [
  { value: "A", label: "Clase A" },
  { value: "B", label: "Clase B" },
  { value: "C", label: "Clase C" },
];

const XYZ_OPTIONS: CheckboxOption[] = [
  { value: "X", label: "X — Predecible" },
  { value: "Y", label: "Y — Moderada" },
  { value: "Z", label: "Z — Impredecible" },
];

const DEMAND_OPTIONS: CheckboxOption[] = [
  { value: "smooth", label: "Suave" },
  { value: "erratic", label: "Errática" },
  { value: "intermittent", label: "Intermitente" },
  { value: "lumpy", label: "Lumpy" },
];

const LIFECYCLE_OPTIONS: CheckboxOption[] = [
  { value: "active", label: "Activo" },
  { value: "stable", label: "Estable" },
  { value: "declining", label: "En declive" },
  { value: "new", label: "Nuevo" },
];

const DCI_OPTIONS: CheckboxOption[] = [
  { value: "full_coverage", label: "Cobertura total" },
  { value: "partial_high", label: "Parcial alta" },
  { value: "partial_low", label: "Parcial baja" },
  { value: "minimal", label: "Mínima" },
];

function productMatchesGroup(
  p: ProductData,
  group: AdvancedFilterGroup,
  set: Set<string>,
): boolean {
  if (set.size === 0) return true;
  switch (group) {
    case "abc":
      return set.has(p.abcClassification ?? "");
    case "xyz":
      return set.has(p.xyzClassification ?? "");
    case "demand":
      return set.has(p.demandClassification ?? "");
    case "lifecycle":
      return set.has(p.lifecycleStatus ?? "");
    case "dci":
      return set.has(p.dciStatus ?? "");
  }
}

function valueExtractor(p: ProductData, group: AdvancedFilterGroup): string {
  switch (group) {
    case "abc":
      return p.abcClassification ?? "";
    case "xyz":
      return p.xyzClassification ?? "";
    case "demand":
      return p.demandClassification ?? "";
    case "lifecycle":
      return p.lifecycleStatus ?? "";
    case "dci":
      return p.dciStatus ?? "";
  }
}

/**
 * Para cada grupo + valor, calcula cuántos productos pasarían los DEMÁS
 * filtros activos + tienen ese valor. Permite al usuario ver el impacto
 * de cada checkbox sin colapsar a 0 por intersección con su propio grupo.
 */
function buildCounts(
  products: ProductData[],
  filters: AdvancedFilters,
): Record<AdvancedFilterGroup, Record<string, number>> & { activeOnly: number } {
  const groups: AdvancedFilterGroup[] = ["abc", "xyz", "demand", "lifecycle", "dci"];
  const result = {
    abc: {} as Record<string, number>,
    xyz: {} as Record<string, number>,
    demand: {} as Record<string, number>,
    lifecycle: {} as Record<string, number>,
    dci: {} as Record<string, number>,
    activeOnly: 0,
  };

  for (const p of products) {
    if (filters.activeOnly && p.activeForPurchase === false) {
      // skip products excluded by activeOnly when computing per-group counts
    }
  }

  for (const targetGroup of groups) {
    for (const p of products) {
      // Aplica TODOS los filtros excepto `targetGroup`.
      let pass = true;
      for (const g of groups) {
        if (g === targetGroup) continue;
        if (!productMatchesGroup(p, g, filters[g])) {
          pass = false;
          break;
        }
      }
      if (pass && filters.activeOnly && p.activeForPurchase === false) {
        pass = false;
      }
      if (!pass) continue;
      const v = valueExtractor(p, targetGroup);
      result[targetGroup][v] = (result[targetGroup][v] ?? 0) + 1;
    }
  }

  // activeOnly count = productos que pasan todos los demás filtros y son active.
  for (const p of products) {
    let pass = true;
    for (const g of groups) {
      if (!productMatchesGroup(p, g, filters[g])) {
        pass = false;
        break;
      }
    }
    if (pass && p.activeForPurchase !== false) {
      result.activeOnly += 1;
    }
  }

  return result;
}

export function SideFilters(props: SideFiltersProps): React.ReactElement {
  const {
    open,
    onClose,
    advancedFilters,
    onToggleFilter,
    onToggleActiveOnly,
    onClear,
    products,
  } = props;

  const counts = useMemo(
    () => buildCounts(products, advancedFilters),
    [products, advancedFilters],
  );
  const activeCount = countActiveAdvancedFilters(advancedFilters);

  return (
    <Box
      data-testid="forecast-side-filters"
      data-open={open ? "true" : "false"}
      sx={{
        width: open ? 260 : 0,
        bgcolor: "var(--surface)",
        borderLeft: "1px solid var(--border)",
        overflow: "hidden",
        transition: "width 0.22s ease",
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          width: 260,
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          gap="8px"
          sx={{
            padding: "12px 14px",
            borderBottom: "1px solid var(--border)",
            flexShrink: 0,
          }}
        >
          <FilterListIcon sx={{ fontSize: 14, color: "var(--ink-3)" }} />
          <Box
            component="h2"
            sx={{
              fontSize: 13,
              fontWeight: 600,
              flex: 1,
              m: 0,
              color: "var(--ink-1)",
            }}
          >
            Filtros avanzados
          </Box>
          {activeCount > 0 && (
            <>
              <Box
                component="span"
                sx={{
                  bgcolor: "var(--accent)",
                  color: "#fff",
                  fontSize: 10.5,
                  fontWeight: 600,
                  padding: "1px 6px",
                  borderRadius: "10px",
                  minWidth: 16,
                  textAlign: "center",
                }}
              >
                {activeCount}
              </Box>
              <Box
                component="button"
                type="button"
                onClick={onClear}
                data-testid="forecast-side-filters-clear"
                sx={{
                  padding: "3px 8px",
                  fontSize: 11.5,
                  border: "1px solid var(--border)",
                  borderRadius: "7px",
                  bgcolor: "transparent",
                  color: "var(--ink-2)",
                  cursor: "pointer",
                  fontFamily: "inherit",
                  "&:hover": {
                    bgcolor: "var(--bg-2)",
                    color: "var(--ink-1)",
                  },
                }}
              >
                Limpiar
              </Box>
            </>
          )}
          <IconButton
            onClick={onClose}
            aria-label="Cerrar filtros"
            data-testid="forecast-side-filters-close"
            size="small"
            sx={{ width: 26, height: 26, color: "var(--ink-3)" }}
          >
            <CloseIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Stack>

        <Box
          sx={{
            overflowY: "auto",
            padding: "8px 4px",
            flex: 1,
            minHeight: 0,
          }}
        >
          <FGroup label="Clase ABC">
            {ABC_OPTIONS.map((o) => (
              <FCheck
                key={o.value}
                checked={advancedFilters.abc.has(o.value)}
                label={o.label}
                count={counts.abc[o.value] ?? 0}
                testId={`sf-abc-${o.value}`}
                onClick={() => onToggleFilter("abc", o.value)}
              />
            ))}
          </FGroup>
          <FGroup label="Predictibilidad XYZ">
            {XYZ_OPTIONS.map((o) => (
              <FCheck
                key={o.value}
                checked={advancedFilters.xyz.has(o.value)}
                label={o.label}
                count={counts.xyz[o.value] ?? 0}
                testId={`sf-xyz-${o.value}`}
                onClick={() => onToggleFilter("xyz", o.value)}
              />
            ))}
          </FGroup>
          <FGroup label="Patrón de demanda">
            {DEMAND_OPTIONS.map((o) => (
              <FCheck
                key={o.value}
                checked={advancedFilters.demand.has(o.value)}
                label={o.label}
                count={counts.demand[o.value] ?? 0}
                testId={`sf-demand-${o.value}`}
                onClick={() => onToggleFilter("demand", o.value)}
              />
            ))}
          </FGroup>
          <FGroup label="Lifecycle">
            {LIFECYCLE_OPTIONS.map((o) => (
              <FCheck
                key={o.value}
                checked={advancedFilters.lifecycle.has(o.value)}
                label={o.label}
                count={counts.lifecycle[o.value] ?? 0}
                testId={`sf-lifecycle-${o.value}`}
                onClick={() => onToggleFilter("lifecycle", o.value)}
              />
            ))}
          </FGroup>
          <FGroup label="Cobertura DCI">
            {DCI_OPTIONS.map((o) => (
              <FCheck
                key={o.value}
                checked={advancedFilters.dci.has(o.value)}
                label={o.label}
                count={counts.dci[o.value] ?? 0}
                testId={`sf-dci-${o.value}`}
                onClick={() => onToggleFilter("dci", o.value)}
              />
            ))}
          </FGroup>
          <FGroup label="Otros" last>
            <FCheck
              checked={advancedFilters.activeOnly}
              label="Solo compras activas"
              count={counts.activeOnly}
              testId="sf-activeOnly"
              onClick={() => onToggleActiveOnly(!advancedFilters.activeOnly)}
            />
          </FGroup>
        </Box>
      </Box>
    </Box>
  );
}

function FGroup({
  label,
  children,
  last = false,
}: {
  label: string;
  children: React.ReactNode;
  last?: boolean;
}): React.ReactElement {
  return (
    <Box
      sx={{
        padding: "10px 14px",
        borderBottom: last ? 0 : "1px solid var(--border)",
      }}
    >
      <Box
        sx={{
          fontSize: 11,
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          color: "var(--ink-3)",
          fontWeight: 600,
          mb: "8px",
        }}
      >
        {label}
      </Box>
      {children}
    </Box>
  );
}

function FCheck({
  checked,
  label,
  count,
  onClick,
  testId,
}: {
  checked: boolean;
  label: string;
  count: number;
  onClick: () => void;
  testId?: string;
}): React.ReactElement {
  return (
    <Stack
      direction="row"
      alignItems="center"
      gap="8px"
      data-testid={testId}
      onClick={onClick}
      role="checkbox"
      aria-checked={checked}
      tabIndex={0}
      onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === " " || e.key === "Enter") {
          e.preventDefault();
          onClick();
        }
      }}
      sx={{
        padding: "4px 0",
        fontSize: 13,
        color: checked ? "var(--ink-1)" : "var(--ink-2)",
        cursor: "pointer",
        "&:hover": { color: "var(--ink-1)" },
      }}
    >
      <Box
        sx={{
          width: 14,
          height: 14,
          border: "1px solid var(--border-strong)",
          borderRadius: "3px",
          display: "grid",
          placeItems: "center",
          flexShrink: 0,
          bgcolor: checked ? "var(--accent)" : "var(--surface)",
          borderColor: checked ? "var(--accent)" : "var(--border-strong)",
          color: "#fff",
          fontSize: 10,
          lineHeight: 1,
        }}
      >
        {checked ? "✓" : ""}
      </Box>
      <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
        {label}
      </Box>
      <Box
        component="span"
        sx={{
          fontSize: 11,
          color: "var(--ink-3)",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {count}
      </Box>
    </Stack>
  );
}

// Re-export helper for tests / orchestrator that need a fresh AdvancedFilters.
export { emptyAdvancedFilters };
